import { useState } from 'react';
import { apiPost } from '../api/client';
import { useOperationOverview } from '../hooks/useOperationOverview';
import { usePollingResource } from '../hooks/usePollingResource';
import { PageHeader, StatusChip } from '../components/ui/Primitives';
import ResourceNotice from '../components/ResourceNotice';
import SaveTickerCredentialCard from '../components/SaveTickerCredentialCard';
import { fmtKst } from '../lib/format';
import { Settings, ShieldAlert, ShieldCheck, Flame } from 'lucide-react';
interface CommandStatus {
    command_id: string | null;
    status: string;
    requested_at?: string;
    started_at?: string;
    completed_at?: string;
    closed_positions?: number;
    error?: string;
}
export default function Control() {
    const operation = useOperationOverview();
    const command = usePollingResource<CommandStatus>('/control/flatten/status', 5000);
    const legacy = usePollingResource<Record<string, unknown>>('/control/params', 60000);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const data = operation.overview;
    const known = !!data && !operation.error && !data.stale && data.kill_switch != null;
    const killSwitch = known ? data!.kill_switch : null;
    const pending = ['REQUESTED', 'RUNNING'].includes(command.data?.status ?? '');
    const halt = async (resume: boolean) => {
        setBusy(true);
        try {
            await apiPost(`/control/killswitch/${resume ? 'resume' : 'halt'}`);
            setMessage(resume ? '신규 진입 차단 해제 요청 완료 · 아래 조회 결과에서 적용 상태를 확인합니다.' : '신규 진입 중단 요청 완료 · 기존 포지션은 자동으로 청산되지 않습니다.');
            operation.refresh();
        }
        catch {
            setMessage('킬스위치 요청 실패 · 현재 조회 상태를 확인해 주세요.');
        }
        finally {
            setBusy(false);
        }
    };
    const flatten = async () => {
        if (!confirm('현재 엔진의 열린 포지션을 모두 청산하도록 요청하시겠습니까? C1은 PAPER 모의 체결입니다.'))
            return;
        setBusy(true);
        try {
            const result = await apiPost<{
                command_id: string;
                status: string;
            }>('/control/flatten');
            setMessage(`전량 청산 요청 접수 · ${result.command_id} · 완료 여부는 코어 처리 결과에서 확인합니다.`);
            command.refresh();
        }
        catch {
            setMessage('전량 청산 요청 실패 · 명령 상태를 확인해 주세요.');
        }
        finally {
            setBusy(false);
        }
    };
    return <div className="cockpit-page">
    <PageHeader icon={<Settings size={22}/>} title="운용 제어" description="신규 진입 차단 · 코어 청산 명령 · 적용 상태와 완료 결과"/>
    <ResourceNotice {...operation}/>
    {message && <p className="notice-card" role="status">{message}</p>}
    <section className="card"><div className="card-title-row"><h3 className="card-title">현재 운용과 비상 제어</h3><StatusChip tone={killSwitch === true ? 'danger' : killSwitch === false ? 'info' : 'warn'}>{killSwitch === true ? '신규 진입 차단' : killSwitch === false ? '신규 진입 조건 평가' : '킬스위치 확인 필요'}</StatusChip></div>
      <p className="monitor-rule">{data?.strategy ?? '전략 미관측'} · {data?.mode ?? '모드 미관측'} · {data?.status_message ?? '실행 상태 조회 중'}</p>
      <dl className="monitor-facts"><div><dt>코어 준비</dt><dd>{data?.core_confirmed ? '확인됨' : '확인 필요'}</dd></div><div><dt>열린 포지션</dt><dd>{data?.positions_confirmed ? data.open_positions_count : '—'}</dd></div><div><dt>원본 관측 · KST</dt><dd>{fmtKst(data?.observed_at)}</dd></div><div><dt>조회 · KST</dt><dd>{fmtKst(data?.as_of)}</dd></div></dl>
      <div className="btn-row"><button className="btn-danger" onClick={() => halt(false)} disabled={busy || killSwitch === true}><ShieldAlert size={16}/>신규 진입 중단</button><button className="btn-success" onClick={() => halt(true)} disabled={busy || killSwitch !== true}><ShieldCheck size={16}/>신규 진입 차단 해제</button><button className="btn-danger" onClick={flatten} disabled={busy || pending}><Flame size={16}/>전량 청산 요청</button></div>
      <p className="muted-note">킬스위치는 신규 진입을 차단합니다. 청산 명령은 koru-core가 처리합니다. 요청 접수와 실제 청산 완료를 구분합니다.</p>
    </section>
    <section className="card"><h3 className="card-title">최근 청산 명령 · 코어 처리 결과</h3><ResourceNotice {...command}/><dl className="monitor-facts">
      <div><dt>상태</dt><dd>{command.error ? '조회 확인 필요' : command.data?.status === 'none' ? '저장된 명령 없음' : command.data?.status ?? '—'}</dd></div><div><dt>완료 청산 수</dt><dd>{command.data?.status === 'SUCCEEDED' ? command.data.closed_positions ?? '—' : '—'}</dd></div><div><dt>명령 ID</dt><dd>{command.data?.command_id ?? '—'}</dd></div><div><dt>요청 · KST</dt><dd>{fmtKst(command.data?.requested_at)}</dd></div><div><dt>시작 · KST</dt><dd>{fmtKst(command.data?.started_at)}</dd></div><div><dt>완료 · KST</dt><dd>{fmtKst(command.data?.completed_at)}</dd></div>
    </dl>{command.data?.error && <p className="research-alert">{command.data.error}</p>}</section>
    <section className="card"><h3 className="card-title">현재 C1 적용 파라미터</h3><dl className="monitor-facts">{Object.entries(data?.strategy_params ?? {}).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl><p className="muted-note">조회 전용입니다. 이 화면의 비상 제어로 전략 파라미터·자본·연구 사양을 변경하지 않습니다.</p></section>
    <SaveTickerCredentialCard />
    <section className="card"><h3 className="card-title">레거시 모델 도구 · 현재 C1에서 미사용</h3><div className="btn-row">{['TFT', 'DRL', 'TimesFM'].map(name => <button key={name} className="btn-secondary" disabled>{name} 재학습 · C1 미사용</button>)}</div><p className="muted-note">기존 재학습 API는 종료한 koru-infer의 플래그 경로입니다. 현재 C1의 학습·배포 명령으로 사용하지 않습니다.</p><details><summary>과거 레짐 가중치 조회</summary><ResourceNotice {...legacy}/><pre className="code-block">{JSON.stringify(legacy.data ?? {}, null, 2)}</pre></details></section>
  </div>;
}
